import { useState } from 'react';
import { api } from '../lib/api';
import { useApi } from '../lib/useApi';
import { money, num } from '../lib/format';
import { Loader, Toast } from '../ui/common';

export default function BalanceSheet({ config, role }) {
  const currency = config.currency || '₹';
  const isOwner = role === 'owner';
  const { data, loading, error, reload } = useApi('/api/balance-sheet');
  const [yi, setYi] = useState(0);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  const pickYear = (i) => { setYi(i); setEdits({}); };
  const valueOf = (item) => (edits[item.row] !== undefined ? edits[item.row] : item.values[yi]);
  const setVal = (row, v) => setEdits((p) => ({ ...p, [row]: v === '' ? 0 : Number(v) }));

  const save = async () => {
    const items = Object.entries(edits).map(([row, value]) => ({ row: Number(row), value: Number(value) }));
    if (items.length === 0) { setToast('No changes made'); return; }
    setBusy(true);
    try {
      await api.put('/api/balance-sheet', { yearIndex: yi, items });
      setEdits({});
      setToast('Balance Sheet updated successfully');
      reload();
    } catch (e) {
      setToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {toast && (
        <Toast message={toast} onDone={() => setToast('')} />
      )}

      {error && <div className="err" style={{ marginBottom: 16 }}>{error}</div>}
      {loading || !data ? <Loader /> : (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            {data.yearLabels.map((y, i) => (
              <button
                key={i}
                type="button"
                style={{
                  padding: '10px 20px',
                  borderRadius: 10,
                  border: yi === i ? 'none' : '1px solid #cbd5e1',
                  background: yi === i ? '#224eae' : '#ffffff',
                  color: yi === i ? '#ffffff' : '#475569',
                  fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: yi === i ? '0 4px 14px rgba(5,150,105,0.25)' : 'none',
                }}
                onClick={() => pickYear(i)}
              >
                {yi === i ? '✓ ' : ''}
                {y}
              </button>
            ))}
          </div>

          <div className="cm-grid-2">
            {data.sections.map((sec) => (
              <div className="cm-form-section-card" key={sec.key}>
                <div className="cm-form-section-title">{sec.title}</div>
                {sec.items.map((item) => (
                  <div key={item.row} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--ink)' }}>{item.label}</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      disabled={!isOwner}
                      value={valueOf(item)}
                      onChange={(e) => setVal(item.row, e.target.value)}
                      style={{ width: 140, padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 8, textAlign: 'right', fontWeight: 700 }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="cm-table-card" style={{ marginTop: 10, marginBottom: 20 }}>
            <div style={{ padding: '16px 20px', fontWeight: 800, fontSize: '0.95rem', color: 'var(--ink)', borderBottom: '1px solid #e2e8f0' }}>
              Totals &amp; Key Ratios — {data.yearLabels[yi]}
            </div>
            <table className="cm-table">
              <thead>
                <tr><th>Metric / Ratio</th><th>Amount / Value</th></tr>
              </thead>
              <tbody>
                <tr><td>Total Assets</td><td style={{ fontWeight: 800, color: 'var(--brand)' }}>{money(data.totals[yi].totalAssets, currency)}</td></tr>
                <tr><td>Total Liabilities</td><td style={{ fontWeight: 800, color: 'var(--ink)' }}>{money(data.totals[yi].totalLiabilities, currency)}</td></tr>
                <tr><td>Owner’s Equity</td><td style={{ fontWeight: 800, color: 'var(--brand)' }}>{money(data.totals[yi].equity, currency)}</td></tr>
                <tr><td>Total Liabilities + Equity</td><td style={{ fontWeight: 800 }}>{money(data.totals[yi].totalLiabilitiesAndEquity, currency)}</td></tr>
                <tr><td>Working Capital</td><td style={{ fontWeight: 800, color: 'var(--brand)' }}>{money(data.totals[yi].workingCapital, currency)}</td></tr>
                <tr><td>Debt Ratio</td><td style={{ fontWeight: 700 }}>{data.totals[yi].debtRatio == null ? '—' : num(data.totals[yi].debtRatio)}</td></tr>
                <tr><td>Current Ratio</td><td style={{ fontWeight: 700 }}>{data.totals[yi].currentRatio == null ? '—' : num(data.totals[yi].currentRatio)}</td></tr>
                <tr>
                  <td>Balance Check (Should be 0)</td>
                  <td style={{ fontWeight: 800, color: data.totals[yi].check !== 0 ? '#0f172a' : '#224eae' }}>
                    {money(data.totals[yi].check, currency)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {isOwner && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="cm-btn-emerald"
                disabled={busy || Object.keys(edits).length === 0}
                onClick={save}
              >
                {busy ? 'Saving...' : `Save ${data.yearLabels[yi]} Balance Sheet`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
